import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogProps, Typography } from '@mui/material';
import { memo, ReactChild } from 'react';
import styled, { css } from 'styled-components';

import { FQ_BUTTON_VARIANTS } from '@/components/muiCustomized/FQButton/constant';
import FQButton from '@/components/muiCustomized/FQButton/FQButton';
import { COLOR, COLOR_FUNCTION, MEDIA_QUERIES_BREAKPOINTS, PX_TO_REM } from '@/globalStyles';

export interface Action {
    label: string;
    onClick?(): void;
    disabled?: boolean;
}

export interface IModalProps extends DialogProps {
    className?: string;
    onClose?: () => void;
    title?: string;
    children: ReactChild;
    hideHeader?: boolean;
    primaryAction?: Action;
    secondaryAction?: Action;
    hideCloseIcon?: boolean;
    hero?: JSX.Element;
    heroTitle?: boolean;
}

const Modal = ({
    className,
    title,
    onClose,
    children,
    hideHeader,
    primaryAction,
    secondaryAction,
    hideCloseIcon,
    hero,
    heroTitle,
    ...props
}: IModalProps) => {
    return (
        <Dialog data-testid="dialog" className={className} {...props} onClose={onClose} scroll="body">
            <div>
                {!hideHeader && (
                    <ModalHeader withoutSeparator={!hero} stacked={!!hero || !!heroTitle}>
                        <ModalHeaderClose>{!hideCloseIcon && <StyledCloseIcon onClick={onClose} />}</ModalHeaderClose>
                        <ModalHeaderTitle heroTitle={!!heroTitle}>
                            {hero}
                            {title ? <Typography variant="modal_or_card_title">{title}</Typography> : null}
                        </ModalHeaderTitle>
                    </ModalHeader>
                )}
                <ModalBody>
                    {children}
                    {primaryAction && (
                        <ButtonsContainer>
                            {secondaryAction && (
                                <FQButton
                                    color="fqRed"
                                    data-variant={FQ_BUTTON_VARIANTS.fqOutline}
                                    onClick={secondaryAction?.onClick}
                                >
                                    <Typography>{secondaryAction.label}</Typography>
                                </FQButton>
                            )}
                            <FQButton
                                color="fqRed"
                                data-variant={FQ_BUTTON_VARIANTS.fqPrimary}
                                onClick={primaryAction.onClick}
                                disabled={primaryAction.disabled}
                            >
                                <Typography>{primaryAction.label}</Typography>
                            </FQButton>
                        </ButtonsContainer>
                    )}
                </ModalBody>
            </div>
        </Dialog>
    );
};

export const ModalBody = styled.div`
    padding: 2rem;
    color: ${COLOR.stormGray};

    @media screen and (width <= ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
        padding: ${PX_TO_REM[20]};
    }
`;

export const ModalHeader = styled.div<{ withoutSeparator: boolean; stacked: boolean }>`
    padding: 1.25rem 2rem;
    ${props =>
        !props.withoutSeparator &&
        css`
            padding-bottom: 0;
        `};
    display: flex;
    flex-direction: ${props => (props.stacked ? `column` : `row-reverse`)};
    flex-wrap: nowrap;
    justify-content: ${props => (props.withoutSeparator ? `space-between` : `flex-end`)};
    ${props =>
        props.withoutSeparator &&
        css`
            border-bottom: 1px solid ${COLOR_FUNCTION.border};
        `}
`;

export const ModalHeaderTitle = styled.div<{ heroTitle: boolean }>`
    ${props =>
        props.heroTitle &&
        css`
            padding-top: 2rem;
        `}
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    align-items: center;
    text-align: center;
`;

export const ModalHeaderClose = styled.div`
    display: flex;
    padding-left: 0.5rem;
`;

const StyledCloseIcon = styled(CloseIcon)`
    margin-left: auto;
    cursor: pointer;
`;

const ButtonsContainer = styled.div`
    padding-top: 2.5rem;
    display: flex;
    flex-wrap: wrap;
    gap: ${PX_TO_REM[10]};
    justify-content: center;

    > button {
        min-width: 7.5rem;
    }

    @media screen and (width <= ${MEDIA_QUERIES_BREAKPOINTS.mobile}px) {
        flex-direction: column;
    }
`;

const Memoized = memo(Modal);
const Styled = styled(Memoized)`
    .MuiDialog-paper {
        max-width: 80vw;
        box-shadow: none;
        border-radius: 8px;
    }
`;

export default Styled;
